import React from "react";
import { Box, Paper, Theme, Typography, useMediaQuery } from "@mui/material";

interface OnTimeRatioProps {
  onTime: number;
  late: number;
  didNotCome: number;
}

const OnTimeRatio: React.FC<OnTimeRatioProps> = ({
  onTime,
  late,
  didNotCome,
}) => {
  const isSmallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const isMediumScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.between("sm", "md")
  );

  const size = isSmallScreen ? 150 : isMediumScreen ? 180 : 200;
  const radius = isSmallScreen ? 60 : isMediumScreen ? 70 : 80;
  const center = size / 2;
  const gap = isSmallScreen ? 16 : 22;
  const strokeWidth = isSmallScreen ? 14 : 20;

  const total = onTime + late + didNotCome;
  const onTimePercentage = (onTime / total) * 100;
  const latePercentage = (late / total) * 100;
  const didNotComePercentage = (didNotCome / total) * 100;

  const circumference = 2 * Math.PI * radius;
  const onTimeDash = (onTimePercentage / 100) * circumference - gap;
  const lateDash = (latePercentage / 100) * circumference - gap;
  const didNotComeDash = (didNotComePercentage / 100) * circumference - gap;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
        On-time Ratio
      </Typography>

      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Box display="flex" justifyContent="center" mb={3}>
          <Box position="relative" width={size} height={size}>
            <svg width="100%" height="100%" viewBox={`0 0 ${size} ${size}`}>
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke="#ffffffff"
                strokeWidth={strokeWidth}
              />
              {/* on time */}
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke="#2196F3"
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={circumference - onTimeDash}
                strokeLinecap="round"
                transform={`rotate(-90 ${center} ${center})`}
              />
              {/* late */}
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke="#B3DEFF"
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={circumference - lateDash}
                strokeLinecap="round"
                transform={`rotate(${
                  (onTimePercentage / 100) * 360 - 90
                } ${center} ${center})`}
              />
              {/* did not come */}
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke="#FF5252"
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={circumference - didNotComeDash}
                strokeLinecap="round"
                transform={`rotate(${
                  ((onTimePercentage + latePercentage) / 100) * 360 - 90
                } ${center} ${center})`}
              />
            </svg>
          </Box>
        </Box>
      </Box>

      {/* Legend */}
      <Box display="flex" flexDirection="column" gap={0.5}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: 0.5,
              backgroundColor: "#2196F3",
            }}
          />
          <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
            On time
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: 0.5,
              backgroundColor: "#B3DEFF",
            }}
          />
          <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
            Late
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: 0.5,
              backgroundColor: "#FF5252",
            }}
          />
          <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
            Did not come
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default OnTimeRatio;
